<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <Header
      @handleLoginSelection="handleLoginSelection"
      @handleSignUpSelection="handleSignUpSelection"
    />

    <div class="header-container">
      <h1 class="page-title">Blog</h1>
      <p class="page-description">
        Discover the latest news and insights from the Skyhost team.
      </p>
    </div>

    <div class="divider-container">
      <Divider />
    </div>

    <div :class="mainContentClass">
      <div class="blogs-grid">
        <div
          v-for="blog in blogs"
          :key="blog.id"
          class="blog-card"
          @click="navigateToBlog(blog.id)"
        >
          <div class="blog-image-container" v-if="blog.image">
            <img :src="blog.image" alt="Blog image" class="blog-image" />
          </div>
          <div class="blog-content">
            <span class="blog-date">{{ blog.date }}</span>
            <h2 class="blog-card-title">{{ blog.title }}</h2>
            <p class="blog-excerpt">{{ blog.excerpt }}</p>
            <div class="blog-footer">
              <span class="continue-reading">Continue reading</span>
              <vue-feather
                type="arrow-right"
                size="16"
                class="arrow-icon"
              ></vue-feather>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />

    <SignUpModal
      :isOpen="isSignUpModalOpen"
      @close="closeSignUpModal"
      :onSubmit="handleSignUp"
      @openLoginModal="openLoginModal"
      :errorMessage="errorMessage"
    />

    <LoginModal
      :isOpen="isLoginModalOpen"
      @close="closeLoginModal"
      @openSignUpModal="openSignUpModal"
      :onSubmit="handleLoginButton"
      :errorMessage="errorMessage"
    />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import { useSessionStore } from "@/stores/session";
import { amplitude } from "../plugins/amplitude";
import { loginService, userAuth } from "../services/userAuth";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import SignUpModal from "@/components/organisms/modal/signUpModal.vue";
import LoginModal from "../components/organisms/modal/loginModal.vue";
import { blogsRegistry } from "@/data/blogRegistry";
import Divider from "@/components/organisms/Divider.vue";

export default {
  components: {
    Header,
    Footer,
    SignUpModal,
    LoginModal,
    Divider,
  },
  data() {
    return {
      blogs: Object.values(blogsRegistry),
      errorMessage: "",
      isLoginModalOpen: false,
      isSignUpModalOpen: false,
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    handleLoginSelection() {
      this.openLoginModal();
    },
    handleSignUpSelection() {
      this.openSignUpModal();
    },
    openLoginModal() {
      this.isLoginModalOpen = true;
    },
    closeLoginModal() {
      this.isLoginModalOpen = false;
      this.errorMessage = "";
    },
    openSignUpModal() {
      this.isSignUpModalOpen = true;
    },
    closeSignUpModal() {
      this.isSignUpModalOpen = false;
      this.errorMessage = "";
    },
    async handleLoginButton(data) {
      amplitude.track("Login Form Submitted", {
        type: "login",
        source: "blog_page",
        email_domain: data.email.split("@")[1] || "unknown",
        timestamp: new Date().toISOString(),
      });

      this.errorMessage = "";

      try {
        const user = await loginService.loginUser(data.email, data.password);
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        amplitude.track("Login Success", {
          userId: user.uid,
          email: user.email,
          source: "blog_page",
          timestamp: new Date().toISOString(),
        });

        this.closeLoginModal();
      } catch (error) {
        this.errorMessage = error.message;

        let errorType = "unknown";
        if (error.message.includes("password")) {
          errorType = "wrong_password";
        } else if (error.message.includes("No account")) {
          errorType = "user_not_found";
        } else if (error.message.includes("network")) {
          errorType = "network_error";
        } else if (error.message.includes("Too many failed")) {
          errorType = "too_many_attempts";
        }

        amplitude.track("Login Failed", {
          error_type: errorType,
          error_message: error.message,
          source: "blog_page",
          timestamp: new Date().toISOString(),
        });
      }
    },
    async handleSignUp(data) {
      this.errorMessage = "";

      amplitude.track("SignUp Form Submitted", {
        type: "signUp",
        source: "blog_page",
        timestamp: new Date().toISOString(),
      });

      try {
        const user = await userAuth.signUpUser(
          data.username,
          data.email,
          data.password,
          data.confirmPassword
        );
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        amplitude.track("SignUp Success", {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || data.username,
          source: "blog_page",
          timestamp: new Date().toISOString(),
        });

        this.closeSignUpModal();
      } catch (error) {
        this.errorMessage = error.message;

        let errorType = "unknown_error";
        if (error.message.includes("already in use")) {
          errorType = "email_already_in_use";
        } else if (error.message.includes("Invalid email")) {
          errorType = "invalid_email";
        } else if (error.message.includes("weak")) {
          errorType = "weak_password";
        } else if (error.message.includes("network")) {
          errorType = "network_error";
        } else if (error.message.includes("match")) {
          errorType = "passwords_dont_match";
        } else if (error.message.includes("fill in all")) {
          errorType = "missing_fields";
        } else if (error.message.includes("disabled")) {
          errorType = "account_disabled";
        }

        amplitude.track("SignUp Failed", {
          errorCode: error.code || "unknown_error",
          errorType: errorType,
          errorMessage: error.message,
          source: "blog_page",
          timestamp: new Date().toISOString(),
        });
      }
    },
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });

    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
  color: var(--text-color);
}

.header-container {
  padding: 2rem 5rem;
  margin-top: 5rem;
}

.page-title {
  color: #000;
  margin-bottom: 1rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 4rem;
  font-weight: 700;
}

.page-description {
  font-size: 1.5rem;
  color: #555;
  font-weight: 300;
  margin-bottom: 1rem;
}

.web-main-content {
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.mobile-main-content {
  padding: 2rem 1rem;
}

.blogs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.blog-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  background-color: white;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.blog-card:hover {
  /* transform: translateY(-5px); */
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.blog-image-container {
  /* height: 200px; */
  overflow: hidden;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.5s ease;
}

.blog-card:hover .blog-image {
  transform: scale(1.05);
}

.blog-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.blog-date {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.blog-card-title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #000;
}

.blog-excerpt {
  color: #555;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  flex-grow: 1;
}

.blog-footer {
  display: flex;
  align-items: center;
  margin-top: auto;
}

.continue-reading {
  color: #000;
  font-weight: 500;
  margin-right: 0.5rem;
}

.arrow-icon {
  color: #000;
}

/* Mobile styles */
@media (max-width: 768px) {
  .blogs-grid {
    grid-template-columns: 1fr;
  }

  .header-container {
    padding: 2rem 1.5rem;
  }

  .divider-container {
    padding: 0 1.5rem;
  }

  .page-title {
    font-size: 2.5rem;
  }
}

.divider-container {
  padding: 0 5rem;
}
</style>
